import React, { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaStar, FaChevronLeft, FaChevronRight, FaLinkedin, FaGoogle } from "react-icons/fa";
import API from "../../api";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: "-50px" });

  useEffect(() => {
    API.get("/testimonials").then(({ data }) => { setTestimonials(data); setCurrent(0); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const timer = setInterval(() => setCurrent((prev) => (prev + 1) % testimonials.length), 4500);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);

  if (testimonials.length === 0) return null;

  return (
    <section id="testimonials" className="pt-4 sm:pt-6 pb-8 sm:pb-10 relative overflow-hidden">
      <div className="absolute inset-0 hero-gradient" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-6 sm:mb-10">
          <span className="text-blue-400 font-semibold text-xs sm:text-sm uppercase tracking-widest">Client Success Stories</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-3 mb-4">
            Trusted by <span className="gradient-text">Industry Leaders</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
            Real results from real businesses. See how we've helped companies across India achieve their digital transformation goals.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="max-w-5xl mx-auto mb-4 sm:mb-6 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-7 relative overflow-hidden"
            >
              {/* Enhanced Background */}
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(circle at 30% 20%, rgba(14,165,233,0.1), transparent 50%), radial-gradient(circle at 70% 80%, rgba(139,92,246,0.08), transparent 50%)" }} />

              {/* Header with Avatar and Info */}
              <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8 relative z-10">
                <img 
                  src={testimonials[current].avatar}
                  alt={testimonials[current].name}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white/20 shadow-lg"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${testimonials[current].name}&background=0ea5e9&color=fff&size=80`;
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-white font-bold text-lg sm:text-xl">{testimonials[current].name}</h3>
                    {testimonials[current].verified && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 rounded-full">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                        <span className="text-green-400 text-xs font-semibold">Verified</span>
                      </div>
                    )}
                  </div>
                  <p className="text-blue-400 font-semibold text-sm sm:text-base">{testimonials[current].role}</p>
                  <p className="text-gray-400 text-sm">{testimonials[current].company}</p>
                  <p className="text-gray-500 text-xs mt-1">{testimonials[current].city}</p>
                  
                  {/* Rating and Platform */}
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex gap-1">
                      {Array.from({ length: testimonials[current].rating }).map((_, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.08 }}
                        >
                          <FaStar className="text-yellow-400 text-sm" />
                        </motion.span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-400">
                      {testimonials[current].platform === 'Google' ? <FaGoogle className="text-xs" /> : <FaLinkedin className="text-xs" />}
                      <span className="text-xs">{testimonials[current].platform} Review</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-gray-300 text-base sm:text-lg leading-relaxed relative z-10 pl-4 border-l-2 border-blue-500/30">
                "{testimonials[current].text}"
              </blockquote>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <button onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 w-12 h-12 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 group">
            <FaChevronLeft className="text-sm group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <button onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 w-12 h-12 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 group">
            <FaChevronRight className="text-sm group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          {testimonials.map((_, i) => (
            <motion.button
              key={i}
              onClick={() => setCurrent(i)}
              animate={{ width: i === current ? 28 : 12 }}
              className={`h-3 rounded-full transition-colors ${i === current ? "gradient-bg" : "bg-white/20"}`}
            />
          ))}
        </div>

        {/* Enhanced Mini Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              whileHover={{ y: -4, scale: 1.02 }}
              onClick={() => setCurrent(i)}
              className={`glass rounded-xl p-3 sm:p-4 cursor-pointer transition-all duration-300 group ${
                i === current 
                  ? "border border-blue-500/50 shadow-lg shadow-blue-500/20 bg-blue-500/5" 
                  : "hover:bg-white/5 hover:border-white/20"
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <img 
                  src={t.avatar}
                  alt={t.name}
                  className="w-8 h-8 rounded-full object-cover border border-white/20"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${t.name}&background=0ea5e9&color=fff&size=32`;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-white text-xs font-semibold truncate">{t.name}</div>
                  <div className="text-gray-400 text-xs truncate">{t.company}</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <FaStar key={j} className="text-yellow-400 text-xs" />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {t.platform === 'Google' ? <FaGoogle className="text-xs text-gray-400" /> : <FaLinkedin className="text-xs text-gray-400" />}
                  {t.verified && <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
